import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { canAccessPrivacyModule } from '../../utils/privacyAccess'
import { canAccessPortalHealth } from '../../utils/portalHealthAccess'

// Logos oficiales de SmartCFO (mismos que el portal)
const logoUrl = 'https://secure-smartcfo.s3.us-east-2.amazonaws.com/images/public/logo/logo-full.png'
const iconUrl = 'https://www.smartcfo.cl/wp-content/uploads/2023/01/Group-138.png'

export function Sidebar({ userEmail, onLogout }) {
  const [hover, setHover] = useState(false)
  const location = useLocation()

  const getInitials = (email) => {
    if (!email) return 'A'
    return email.charAt(0).toUpperCase()
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home', path: '/dashboard' },
    {
      id: 'reconciliation-progress',
      label: 'Avance Conciliación',
      icon: 'barChart',
      path: '/avance-conciliacion',
    },
    { id: 'companies', label: 'Empresas', icon: 'briefcase', path: '/empresas' },
    { id: 'users', label: 'Usuarios', icon: 'users', path: '/usuarios' },
    { id: 'bulk-permissions', label: 'Permisos Masivos', icon: 'shield', path: '/permisos-masivos' },
    { id: 'communications', label: 'Comunicaciones', icon: 'megaphone', path: '/comunicaciones' },
    { id: 'signin-themes', label: 'Apariencia del Login', icon: 'layout', path: '/apariencia-login' },
    { id: 'role-ip-whitelist', label: 'Whitelist IP', icon: 'globe', path: '/whitelist-ip' },
    ...(canAccessPrivacyModule()
      ? [
          { id: 'privacy', label: 'Privacidad', icon: 'privacy', path: '/privacidad' },
          {
            id: 'privacy-subject-search',
            label: 'Baja de usuario',
            icon: 'search',
            path: '/privacidad/localizar',
          },
        ]
      : []),
    ...(canAccessPortalHealth(userEmail)
      ? [{ id: 'portal-health', label: 'Salud', icon: 'activity', path: '/salud' }]
      : []),
  ]

  // Determinar qué item está activo basado en la ruta
  const getActiveItem = () => {
    const path = location.pathname
    if (path.startsWith('/avance-conciliacion')) return 'reconciliation-progress'
    if (path.startsWith('/comunicaciones')) return 'communications'
    if (path.startsWith('/apariencia-login')) return 'signin-themes'
    if (path.startsWith('/privacidad/localizar')) return 'privacy-subject-search'
    if (path.startsWith('/privacidad')) return 'privacy'
    if (path.startsWith('/salud')) return 'portal-health'
    if (path.startsWith('/whitelist-ip')) return 'role-ip-whitelist'
    if (path.startsWith('/permisos-masivos')) return 'bulk-permissions'
    if (path.startsWith('/empresas')) return 'companies'
    if (path.startsWith('/usuarios')) return 'users'
    if (path === '/dashboard' || path === '/') return 'dashboard'
    return 'dashboard'
  }

  const activeItem = getActiveItem()

  // Iconos SVG similares a Feather Icons
  const icons = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    ),
    briefcase: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
      </svg>
    ),
    users: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    barChart: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10"></line>
        <line x1="18" y1="20" x2="18" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
      </svg>
    ),
    privacy: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        <polyline points="9 12 11 14 15 10"></polyline>
      </svg>
    ),
    search: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.3-4.3"></path>
      </svg>
    ),
    activity: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
      </svg>
    ),
    shield: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    ),
    megaphone: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 11 18-5v12L3 13v-2z"></path>
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
      </svg>
    ),
    layout: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="9" y1="3" x2="9" y2="21"></line>
      </svg>
    ),
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
      </svg>
    ),
    logout: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
    )
  }

  // Sidebar solo por hover (sin pin/clip)
  const isExpanded = hover

  return (
    <aside 
      className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Header con Logo */}
      <div className="sidebar-header">
        <div className="sidebar-logo-container">
          {!isExpanded ? (
            <img src={iconUrl} alt="SmartCFO" className="sidebar-icon-img" />
          ) : (
            <>
              <img src={logoUrl} alt="SmartCFO" className="sidebar-logo-img" />
              <span className="sidebar-subtitle">Panel de Administración</span>
            </>
          )}
        </div>
      </div>

      {/* Navegación con Links */}
      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <Link
            key={item.id}
            to={item.path}
            className={`nav-item ${activeItem === item.id ? 'active' : ''}`}
            title={!isExpanded ? item.label : undefined}
          >
            <span className="nav-icon">{icons[item.icon]}</span>
            {isExpanded && <span className="nav-label">{item.label}</span>}
          </Link>
        ))}
      </nav>

      {/* Footer con Usuario */}
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">{getInitials(userEmail)}</div>
          {isExpanded && (
            <div className="user-details">
              <div className="user-name">Admin</div>
              <div className="user-email">{userEmail || 'admin@smartcfo.cl'}</div>
            </div>
          )}
        </div>
        <button 
          className="logout-btn" 
          onClick={onLogout}
          title={!isExpanded ? 'Cerrar sesión' : undefined}
        >
          {icons.logout}
          {isExpanded && <span>Cerrar sesión</span>}
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
